/**
 * StageComponent — Toile's page verb (wall plan e § Files; boards `PageVerb.dc.html` and
 * `ToilePageToWall.dc.html`). Three pieces: a field that is always on, up to three authored chips,
 * and the verb, waiting in outline until there are words. Since plan 2026_09_23_pack_stage_coda.md
 * it lives in the stage's coda at every width (the template creates it once and the stage places
 * it; board `TenthFrame.dc.html`), and its words come from `./copy`.
 *
 * Why a field at all: Narcis and Ovid know who the visitor is (a trained model, a chosen muse), so
 * their verb needs no input. Toile has no identity, so the DIRECTION is the input — the same words
 * bent nine ways, three readings × three styles (BRIEF §9). A chip is a SUGGESTION, not a trigger
 * (operator ruling 2026-09-01, reversing "each chip fires"): tapping it writes its words into the
 * field — `pageVerb.withChip` — where they stay editable; only the verb fires.
 *
 * The rules it applies live in `@core/wall/pageVerb` — the lit rule, the wire body, the landing
 * path, and the sign-in token's round-trip — so the same model serves plans f and g and is testable
 * without a DOM (`core/wall/__tests__/pageVerb.spec.ts`). This file holds only what a component
 * must: the field's state, the one-shot submit, and the navigation.
 *
 * The `request_id` is minted BEFORE anything can interrupt (amendment S4) and held per direction for
 * the life of the mount, which is what makes the three ways of firing twice — a double-click, a
 * bfcache Back, a sign-in return — land on plan c's `(userId, requestId, position)` idempotency and
 * produce the same nine rows. A DELIBERATE second press of the same words is idempotent too, and
 * that is correct here: a landed press navigates to the wall, so there is no second press to make.
 *
 * `page_verb` fires on a press — the visitor's own decision — and not on the token's re-submit
 * after sign-in, which completes a press already counted rather than making a new one.
 *
 * The terms gate (plan 2026_09_01_tos_gate.md §8.6 step 3) wraps `submit` itself, not `press`
 * above. The server decides who may write (plan 2026_10_25_toile_first_take_free.md): a visitor's
 * first press writes, a refused one returns 401 and the sign-in card, the press held in the token.
 * Both call sites — the direct press and the sign-in return's re-submit — go through the same
 * `gate.press`, so the terms card comes before the first anonymous row, and it renders between the
 * chips and the verb.
 *
 * DEVIATION from plan e's § Files, which names `useFetcher` for the submit. The write goes through
 * `fetch`, the idiom `Wall.tsx`'s own `fire()` uses against the sibling route `/continue`
 * (`core/wall/ui/Wall.tsx:521`): a fetcher decodes non-2xx bodies, and `errorLineFor` needs both the
 * status and the body to say the one line the verb can say — the same idiom across the wall's write
 * routes.
 */

import {useCallback, useEffect, useRef, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router'

import {track} from '@core/utils/analytics'
import type {ContinuePackResponse, ErrorResponse} from '@core/wall/contract'
import {MAX_DIRECTION_CHARS} from '@core/wall/contract'
import {mintRequestId} from '@core/wall/requestId'
import {errorLineFor, noticeKindFor} from '@core/wall/ui/chains'
import {
    batchWallPath, chipUsed, packContinueBody, packReturnToken, readPackReturnToken, strippedSearch, verbLit,
    withChip,
} from '@core/wall/pageVerb'
import {useConsentGate} from '@core/consent/useConsentGate'
import {TosConsentCard} from '@core/consent/TosConsentCard'
import {Notice} from '@ui/Notice'
import type {ToilePack} from './data'
// Relative, not `@product/wall/...` — the jest config's `@product/(.*)` moduleNameMapper entry
// takes priority over the `.module.scss$` proxy rule (both would match; the alias rule comes
// first), so an aliased `.module.scss` import resolves to the REAL file and jest tries to parse
// SCSS as JS. A relative import falls through to the proxy, same as every other product's own
// `.module.scss` import.
import consentStyles from '../wall/TosConsentCard.module.scss'
import {TOILE_CONSENT_COPY} from '../wall/consentCopy'
import {FIELD_PLACEHOLDER, VERB_LABEL, fieldLabel} from './copy'
import styles from './StageComponent.module.scss'

const CONTINUE_PACK_ACTION = '/continue/pack'

type StageComponentProps = {
    pack: ToilePack
    /** From the route loader. Anonymous, the press still goes to the server: a visitor's first press writes
     *  (their free pick, answered with `free_pick`), a refused one gets the sign-in card. */
    signedIn: boolean
    /** Hands the caller the URL the card must return to — the page's own, carrying the press. */
    onGate: (redirectTo: string) => void
    /** Bumped by the aside's coda door: each new value focuses the field (a deliberate press on the
     *  same page — the one case where a phone keyboard may rise). 0 never focuses. */
    focusToken?: number
}

export function StageComponent({pack, signedIn, onGate, focusToken = 0}: StageComponentProps) {
    const [field, setField] = useState('')
    const [note, setNote] = useState<string | null>(null)
    const [working, setWorking] = useState(false)
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const fieldRef = useRef<HTMLInputElement>(null)
    const chips = pack.directions ?? []

    /** The key minted for the words currently pending. Held rather than re-minted so a second press
     *  of the same words is the same press on the wire. */
    const pendingRef = useRef<{direction: string; requestId: string} | null>(null)
    /** A write in flight. Not the `working` state: a double-click has to be dropped on the very
     *  next press, synchronously, never a render late (`chains.isDuplicatePress` holds its own
     *  press the same way, and for the same reason). It guards the round trip; the held
     *  `request_id` guards everything the client cannot see — a Back, a bfcache restore, a
     *  sign-in return. */
    const inFlightRef = useRef(false)
    /** The mount URL's token, read once (S4). */
    const tokenReadRef = useRef(false)
    const gate = useConsentGate({house: 'toile', surface: 'page_verb'})

    const submit = useCallback(
        async (direction: string, requestId: string) => {
            if (inFlightRef.current) return
            inFlightRef.current = true
            setWorking(true)
            setNote(null)
            try {
                const res = await fetch(CONTINUE_PACK_ACTION, {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(packContinueBody(pack.slug, direction, requestId)),
                    credentials: 'same-origin',
                })
                if (res.status === 401) {
                    // The session went between the loader and the press. The card is still the
                    // answer, and the press is still held, so the return re-submits it.
                    onGate(packReturnToken({packSlug: pack.slug, direction, requestId}))
                    return
                }
                if (!res.ok) {
                    let error: ErrorResponse | null = null
                    try {
                        error = (await res.json()) as ErrorResponse
                    } catch {
                        // A body-less 4xx/5xx: the status alone names the line.
                    }
                    setNote(errorLineFor(res.status, error?.error, error?.field))
                    return
                }
                const data: ContinuePackResponse = await res.json()
                // `page_generate` (decision 4, plan `2026_09_21_wall_gaps.md`) — the write landed,
                // tracked inside the closure that posts the batch so BOTH call sites reach it: the
                // direct press's `gate.press`, and the post-sign-in return effect below. Only on the
                // 200: a 401 (the spent free pick, an expired session) or a refusal is not a generation.
                track('page_generate', {
                    pack_slug: pack.slug,
                    direction_len: direction.trim().length,
                    chip_used: chipUsed(direction, chips),
                })
                if (!signedIn) track('free_pick', {house: 'toile', surface: 'page_verb', kind: 'pack'})
                navigate(batchWallPath(data.batch_id))
            } catch {
                setNote(errorLineFor(0))
            } finally {
                inFlightRef.current = false
                setWorking(false)
            }
        },
        [chips, navigate, onGate, pack.slug, signedIn],
    )

    /** One press: the verb, the only thing that fires (a chip only fills the field —
     *  `pageVerb.withChip`). `chip_used` is read off the body itself, at press time: true when the
     *  submitted direction carries a chip's words, whether that chip filled the field or the visitor
     *  typed the same words — a chip and typed words post the identical body either way.
     *
     *  `page_verb` tracks inside the fired closure, not unconditionally at the top of `press`
     *  (2026-09-01 fix round, item 4, mirroring `Wall.tsx`'s own `fire()` tracking `wall_generate`
     *  inside `gate.press`'s closure) — a press deferred behind the terms gate reaches this
     *  function TWICE (the ask, then the accepting press), and the old unconditional call tracked
     *  both. */
    const press = useCallback(
        (direction: string) => {
            if (!verbLit(direction)) return
            if (inFlightRef.current) return

            const held = pendingRef.current
            const requestId = held && held.direction === direction ? held.requestId : mintRequestId()
            pendingRef.current = {direction, requestId}

            function trackPageVerb() {
                track('page_verb', {
                    house: 'toile',
                    pack_slug: pack.slug,
                    direction_len: direction.trim().length,
                    chip_used: chipUsed(direction, chips),
                })
            }

            // The press survives a refusal: the key is already minted, so the re-submit on return is the
            // same write, not a second one.
            gate.press(() => {
                trackPageVerb()
                return submit(direction, requestId)
            })
        },
        [chips, gate, pack.slug, submit],
    )

    // ── the return from the sign-in card: re-submit once, then strip the params ─────────────────
    useEffect(() => {
        if (tokenReadRef.current) return
        tokenReadRef.current = true
        const token = readPackReturnToken(searchParams, pack.slug)

        // Stripped whatever the params turned out to say, and with `replaceState` rather than a
        // navigation: re-running this route's loader would only re-render the same page.
        const next = strippedSearch(new URLSearchParams(window.location.search))
        const qs = next.toString()
        window.history.replaceState(
            window.history.state,
            '',
            `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`,
        )

        if (!token || !signedIn) return
        // A `[]` effect: it closes over this render's `gate`, so `gate.press` here is whatever this
        // hook returned on the FIRST render — a stale closure by definition. Correct regardless,
        // since `useConsentGate`'s own `press` reads the CURRENT machine state and browser flag off
        // refs it keeps live, not off the render-time values this closure happens to hold (2026-09-01
        // fix round, item 1 — see that hook's own header doc).
        // The field shows what is being re-submitted: the visitor left words behind and comes back
        // to them, not to an empty page mid-write.
        setField(token.direction)
        pendingRef.current = {direction: token.direction, requestId: token.requestId}
        gate.press(() => submit(token.direction, token.requestId))
        // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once against the mount URL only
    }, [])

    useEffect(() => {
        if (focusToken === 0) return
        // The template brings the stage into view; the field only takes focus.
        fieldRef.current?.focus({preventScroll: true})
    }, [focusToken])

    const lit = verbLit(field)

    /** A press on a chip or the verb keeps the field's focus: the coda folds its head while it holds
     *  focus (`StageCoda`'s `:focus-within`), and a blur on pointer-down would unfold it and move
     *  the verb out from under the pointer before the click lands. */
    function keepFieldFocus(e: React.MouseEvent) {
        if (document.activeElement === fieldRef.current) e.preventDefault()
    }

    return (
        <div className={styles.stage}>
            <div className={styles.fieldRow}>
                <input
                    ref={fieldRef}
                    type="text"
                    className={styles.field}
                    value={field}
                    onChange={(e) => setField(e.target.value)}
                    maxLength={MAX_DIRECTION_CHARS}
                    onKeyDown={(e) => {
                        if (e.key !== 'Enter') return
                        e.preventDefault()
                        press(field)
                    }}
                    placeholder={FIELD_PLACEHOLDER}
                    aria-label={fieldLabel(pack.name)}
                    enterKeyHint="go"
                />
            </div>

            {chips.length > 0 && (
                <div className={styles.chips}>
                    {chips.map((chip) => (
                        <button
                            key={chip}
                            type="button"
                            className={styles.chip}
                            // A suggestion, not a press: it only fills the field, so there is
                            // nothing here for `working` to block. Disabled anyway while a write is
                            // in flight — the same reason the verb is — so the field reads as
                            // settled until that write resolves.
                            disabled={working}
                            onMouseDown={keepFieldFocus}
                            onClick={() => {
                                setField((prev) => withChip(prev, chip))
                                fieldRef.current?.focus()
                            }}
                        >
                            {chip}
                        </button>
                    ))}
                </div>
            )}

            {gate.open && (
                <div className={styles.consent}>
                    <TosConsentCard
                        styles={consentStyles}
                        checked={gate.checked}
                        onTick={gate.tick}
                        focusToken={gate.cardProps.focusToken}
                        termsHref="/terms"
                        privacyHref="/privacy"
                        copy={TOILE_CONSENT_COPY}
                    />
                </div>
            )}

            <button
                type="button"
                // At rest the verb waits in outline (accent words, no fill), never faded: it is
                // waiting, not broken. Filled once there are words; `disabled` carries both states
                // to assistive technology, as before.
                className={`${styles.verb} ${!lit ? styles.verbWaiting : working ? styles.verbWorking : ''}`}
                onMouseDown={keepFieldFocus}
                onClick={() => press(field)}
                disabled={!lit || working}
            >
                {VERB_LABEL}
            </button>

            {note && <Notice kind={noticeKindFor(note)} className={styles.note} ttlMs={0}>{note}</Notice>}
        </div>
    )
}
